import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@halo/web-lib';
import {
  Play,
  Loader2,
  RefreshCw,
  Clock,
  AlertCircle,
  CheckCircle2,
  Pause,
  Timer,
  Trash2,
  ChevronDown,
  ChevronRight,
  Zap,
  X,
  AlertTriangle,
  ChevronsDown,
  ChevronsUp,
  Search,
} from 'lucide-react';
import { InfoBadge } from '../components/ui/Tooltip';

/* ─── Types ───────────────────────────────────── */

interface QueueScheduler {
  name: string;
  pattern: string | null;
  next: number | undefined;
}

interface QueueOverview {
  name: string;
  counts: { waiting: number; active: number; completed: number; failed: number; delayed: number };
  schedulers: QueueScheduler[];
}

interface QueueJob {
  id: string;
  name: string;
  data: Record<string, unknown>;
  failedReason?: string;
  attemptsMade?: number;
  timestamp?: number;
  finishedOn?: number;
  progress?: number;
  delay?: number;
}

interface QueueDetail {
  queueName: string;
  counts: { waiting: number; active: number; completed: number; failed: number; delayed: number };
  failed: QueueJob[];
  completed: QueueJob[];
  waiting: QueueJob[];
  active: QueueJob[];
  delayed: QueueJob[];
}

/* ─── Queue metadata ──────────────────────────── */

const QUEUE_META: Record<string, { label: string; description: string }> = {
  'backfill':        { label: 'Backfill',        description: 'Historical data ingestion (month/day chunks)' },
  'daily-pull':      { label: 'Daily Pull',      description: 'Scheduled per-store daily data pulls' },
  'gap-backfill':    { label: 'Gap Backfill',    description: 'Auto-fills missed days in current month' },
  'pull-ad-metrics': { label: 'Pull Ad Metrics', description: 'Legacy ad metrics worker (migration)' },
  'pull-orders':     { label: 'Pull Orders',     description: 'Legacy order pull worker (migration)' },
};

/* ─── Helpers ─────────────────────────────────── */

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function timeUntil(ts: number): string {
  const diff = ts - Date.now();
  if (diff <= 0) return 'now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `in ${hrs}h ${mins % 60}m`;
}

function jobSummary(job: QueueJob): string {
  const d = job.data;
  const parts: string[] = [];
  if (d['platform']) parts.push(String(d['platform']));
  if (d['storeId']) parts.push(`store:${d['storeId']}`);
  if (d['startDate'] && d['endDate']) {
    parts.push(d['startDate'] === d['endDate'] ? String(d['startDate']) : `${d['startDate']} - ${d['endDate']}`);
  } else if (d['month']) {
    parts.push(String(d['month']));
  }
  return parts.length > 0 ? parts.join(' / ') : job.name;
}

function queueHealthColor(q: QueueOverview): string {
  if (q.counts.failed > 0) return 'bg-red-500';
  if (q.counts.active > 0) return 'bg-blue-500 animate-pulse';
  if (q.counts.waiting > 0) return 'bg-amber-500';
  return 'bg-green-500';
}

/* ─── Toast ───────────────────────────────────── */

function useToast() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  return { toast, showToast: setToast };
}

function ToastBanner({ message, type, onDismiss }: { message: string; type: 'success' | 'error'; onDismiss: () => void }) {
  return (
    <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
      type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}>
      {type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onDismiss} className="ml-2 opacity-70 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

/* ─── Main Component ──────────────────────────── */

export function JobsPage() {
  const [queues, setQueues] = useState<QueueOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedQueues, setExpandedQueues] = useState<Set<string>>(new Set());
  const [queueDetails, setQueueDetails] = useState<Map<string, QueueDetail>>(new Map());
  const [detailLoading, setDetailLoading] = useState<Set<string>>(new Set());
  const [acting, setActing] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [failedSearch, setFailedSearch] = useState('');
  const { toast, showToast } = useToast();

  const fetchOverview = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/ingestion/admin/jobs');
      if (res.success && res.data) {
        setQueues((res.data as { queues: QueueOverview[] }).queues);
        setLastRefresh(Date.now());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    const interval = setInterval(fetchOverview, 15_000);
    return () => clearInterval(interval);
  }, [fetchOverview]);

  // Auto-refresh indicator countdown
  const [refreshCountdown, setRefreshCountdown] = useState(15);
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshCountdown(Math.max(0, 15 - Math.floor((Date.now() - lastRefresh) / 1000)));
    }, 1000);
    return () => clearInterval(interval);
  }, [lastRefresh]);

  async function fetchQueueDetail(queueName: string) {
    setDetailLoading((prev) => new Set([...prev, queueName]));
    try {
      const res = await api.get(`/api/v1/ingestion/admin/queue-status/${queueName}`);
      if (res.success && res.data) {
        setQueueDetails((prev) => new Map([...prev, [queueName, res.data as QueueDetail]]));
      }
    } finally {
      setDetailLoading((prev) => {
        const next = new Set(prev);
        next.delete(queueName);
        return next;
      });
    }
  }

  function toggleQueue(name: string) {
    setExpandedQueues((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
        if (!queueDetails.has(name)) fetchQueueDetail(name);
      }
      return next;
    });
  }

  function expandAll() {
    const all = new Set(queues.map((q) => q.name));
    setExpandedQueues(all);
    for (const name of all) {
      if (!queueDetails.has(name)) fetchQueueDetail(name);
    }
  }

  function collapseAll() {
    setExpandedQueues(new Set());
  }

  async function handleTrigger(queueName: string) {
    setActing(`trigger-${queueName}`);
    try {
      await api.post('/api/v1/ingestion/admin/trigger-job', { queueName });
      showToast({ message: `${QUEUE_META[queueName]?.label ?? queueName} triggered`, type: 'success' });
      await fetchOverview();
      if (expandedQueues.has(queueName)) await fetchQueueDetail(queueName);
    } finally {
      setActing(null);
    }
  }

  async function handleRetry(queueName: string, jobId: string) {
    setActing(`retry-${jobId}`);
    try {
      await api.post('/api/v1/ingestion/admin/retry-job', { queueName, jobId });
      showToast({ message: 'Job retried', type: 'success' });
      if (expandedQueues.has(queueName)) await fetchQueueDetail(queueName);
      await fetchOverview();
    } finally {
      setActing(null);
    }
  }

  async function handleRemove(queueName: string, jobId: string) {
    setActing(`remove-${jobId}`);
    try {
      await api.post('/api/v1/ingestion/admin/remove-job', { queueName, jobId });
      showToast({ message: 'Job removed', type: 'success' });
      if (expandedQueues.has(queueName)) await fetchQueueDetail(queueName);
      await fetchOverview();
    } finally {
      setActing(null);
    }
  }

  async function handleRetryAllFailed(queueName: string) {
    setActing(`retry-all-${queueName}`);
    try {
      await api.post('/api/v1/ingestion/admin/retry-all-failed', { queueName });
      showToast({ message: 'All failed jobs retried', type: 'success' });
      if (expandedQueues.has(queueName)) await fetchQueueDetail(queueName);
      await fetchOverview();
    } finally {
      setActing(null);
    }
  }

  async function handleRemoveAllFailed(queueName: string) {
    setActing(`remove-all-${queueName}`);
    try {
      await api.post('/api/v1/ingestion/admin/remove-all-failed', { queueName });
      showToast({ message: 'All failed jobs removed', type: 'success' });
      if (expandedQueues.has(queueName)) await fetchQueueDetail(queueName);
      await fetchOverview();
    } finally {
      setActing(null);
    }
  }

  // Clear failed jobs across every queue that has any. Iterates so we can
  // surface a single combined toast and still refresh once at the end.
  async function handleClearAllFailedGlobal() {
    const queuesWithFailures = queues.filter((q) => q.counts.failed > 0);
    if (queuesWithFailures.length === 0) return;
    const totalFailedBefore = queuesWithFailures.reduce((s, q) => s + q.counts.failed, 0);
    if (
      !confirm(
        `Remove all ${totalFailedBefore} failed job(s) across ${queuesWithFailures.length} queue(s)? This cannot be undone.`,
      )
    ) {
      return;
    }
    setActing('clear-all-failed');
    let okCount = 0;
    let failCount = 0;
    try {
      await Promise.all(
        queuesWithFailures.map(async (q) => {
          try {
            const res = await api.post('/api/v1/ingestion/admin/remove-all-failed', {
              queueName: q.name,
            });
            if (res.success) okCount += 1;
            else failCount += 1;
          } catch {
            failCount += 1;
          }
        }),
      );
      // Refresh details for whatever's expanded so the rows clear in-place.
      await Promise.all(
        [...expandedQueues].map((name) => fetchQueueDetail(name)),
      );
      await fetchOverview();
      showToast({
        message:
          failCount === 0
            ? `Cleared failed jobs across ${okCount} queue(s)`
            : `Cleared ${okCount} queue(s), ${failCount} failed`,
        type: failCount === 0 ? 'success' : 'error',
      });
    } finally {
      setActing(null);
    }
  }

  async function handleDrain(queueName: string) {
    if (!confirm(`Drain all jobs from ${queueName}?`)) return;
    setActing(`drain-${queueName}`);
    try {
      await api.post('/api/v1/ingestion/admin/drain-queue', { queueName });
      showToast({ message: `${QUEUE_META[queueName]?.label ?? queueName} drained`, type: 'success' });
      if (expandedQueues.has(queueName)) await fetchQueueDetail(queueName);
      await fetchOverview();
    } finally {
      setActing(null);
    }
  }

  async function handleSyncSchedules() {
    setActing('sync-schedules');
    try {
      await api.post('/api/v1/ingestion/admin/sync-schedules', {});
      showToast({ message: 'Schedules synced', type: 'success' });
      await fetchOverview();
    } finally {
      setActing(null);
    }
  }

  // Totals
  const totalActive = queues.reduce((s, q) => s + q.counts.active, 0);
  const totalFailed = queues.reduce((s, q) => s + q.counts.failed, 0);
  const totalWaiting = queues.reduce((s, q) => s + q.counts.waiting, 0);
  const totalCompleted = queues.reduce((s, q) => s + q.counts.completed, 0);
  const totalSchedulers = queues.reduce((s, q) => s + q.schedulers.length, 0);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48" />
        <div className="grid grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-xl" />)}
        </div>
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  return (
    <div>
      {toast && <ToastBanner message={toast.message} type={toast.type} onDismiss={() => showToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Job Queues</h2>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Live BullMQ view (Redis). For the durable failure ledger with Replay/Resolve, see{' '}
            <Link to="/failed-jobs" className="text-blue-600 dark:text-blue-400 hover:underline">Failed Jobs</Link>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Auto-refresh indicator */}
          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            {refreshCountdown}s
          </span>
          <button
            onClick={() => expandedQueues.size > 0 ? collapseAll() : expandAll()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            {expandedQueues.size > 0 ? <ChevronsUp className="w-3.5 h-3.5" /> : <ChevronsDown className="w-3.5 h-3.5" />}
            {expandedQueues.size > 0 ? 'Collapse All' : 'Expand All'}
          </button>
          <button
            onClick={handleClearAllFailedGlobal}
            disabled={acting === 'clear-all-failed' || totalFailed === 0}
            title={totalFailed === 0 ? 'No failed jobs to clear' : `Remove all ${totalFailed} failed job(s) from every queue`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {acting === 'clear-all-failed' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Clear all failed{totalFailed > 0 ? ` (${totalFailed})` : ''}
          </button>
          <button
            onClick={handleSyncSchedules}
            disabled={acting === 'sync-schedules'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50"
          >
            {acting === 'sync-schedules' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Sync Schedules
          </button>
          <button
            onClick={fetchOverview}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        <SummaryCard label="Active Jobs" value={totalActive} icon={Zap} color="blue" />
        <SummaryCard label="Waiting" value={totalWaiting} icon={Pause} color="amber" />
        <SummaryCard label="Failed" value={totalFailed} icon={AlertCircle} color={totalFailed > 0 ? 'red' : 'gray'} />
        <SummaryCard label="Completed" value={totalCompleted} icon={CheckCircle2} color="green" />
        <SummaryCard label="Schedulers" value={totalSchedulers} icon={Clock} color="gray" />
      </div>

      {/* Queue List */}
      <div className="space-y-2">
        {queues.map((q) => {
          const meta = QUEUE_META[q.name] ?? { label: q.name, description: '' };
          const isExpanded = expandedQueues.has(q.name);
          const detail = queueDetails.get(q.name);
          const isDetailLoading = detailLoading.has(q.name);
          const healthDot = queueHealthColor(q);

          return (
            <div key={q.name} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
              {/* Queue Header Row */}
              <div
                onClick={() => toggleQueue(q.name)}
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {isExpanded
                    ? <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    : <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  }
                  <InfoBadge tip={
                    q.counts.failed > 0 ? 'Queue has failed jobs that need attention. Check errors and retry or remove.' :
                    q.counts.active > 0 ? 'Queue has active jobs being processed right now.' :
                    q.counts.waiting > 0 ? 'Queue has waiting jobs. Workers may be busy or paused.' :
                    'Queue is healthy. No failed or waiting jobs.'
                  }>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${healthDot}`} />
                  </InfoBadge>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{meta.label}</div>
                    <div className="text-[10px] text-gray-400 dark:text-gray-500">{meta.description}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {/* Counts pills */}
                  <div className="flex items-center gap-1.5">
                    {q.counts.active > 0 && (
                      <InfoBadge tip="Jobs currently being processed by a worker.">
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400">
                          {q.counts.active} active
                        </span>
                      </InfoBadge>
                    )}
                    {q.counts.waiting > 0 && (
                      <InfoBadge tip="Jobs queued and waiting for a worker to pick them up.">
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300">
                          {q.counts.waiting} waiting
                        </span>
                      </InfoBadge>
                    )}
                    {q.counts.failed > 0 && (
                      <InfoBadge tip="Jobs that threw an error. Can be retried or removed.">
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400">
                          {q.counts.failed} failed
                        </span>
                      </InfoBadge>
                    )}
                    {q.counts.delayed > 0 && (
                      <InfoBadge tip="Jobs scheduled to run at a future time (e.g., cron-triggered daily pulls).">
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                          {q.counts.delayed} delayed
                        </span>
                      </InfoBadge>
                    )}
                    <InfoBadge tip="Jobs that completed successfully. Old completed jobs are auto-cleaned.">
                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400">
                        {q.counts.completed} done
                      </span>
                    </InfoBadge>
                  </div>

                  {/* Schedulers badge */}
                  {q.schedulers.length > 0 && (
                    <InfoBadge tip="Number of recurring cron schedules attached to this queue. Each scheduler creates jobs on a fixed pattern.">
                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400">
                        {q.schedulers.length} sched
                      </span>
                    </InfoBadge>
                  )}

                  {/* Trigger button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleTrigger(q.name); }}
                    disabled={acting === `trigger-${q.name}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                    title={`Manually trigger ${meta.label}`}
                  >
                    {acting === `trigger-${q.name}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                    Trigger
                  </button>
                </div>
              </div>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-3 space-y-3 bg-gray-50/50">
                  {/* Schedulers */}
                  {q.schedulers.length > 0 && (
                    <div>
                      <div className="text-[10px] font-medium text-purple-600 dark:text-purple-400 mb-1.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Schedulers
                      </div>
                      <div className="space-y-1">
                        {q.schedulers.map((s) => (
                          <div key={s.name} className="flex items-center justify-between bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-1.5">
                            <div>
                              <span className="text-xs font-medium text-gray-800 dark:text-gray-100">{s.name}</span>
                              <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-2 font-mono">{s.pattern ?? '--'}</span>
                            </div>
                            {s.next && (
                              <span className="text-[10px] text-gray-500 dark:text-gray-400" title={new Date(s.next).toLocaleString()}>
                                {timeUntil(s.next)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Detail jobs */}
                  {isDetailLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400 dark:text-gray-500" />
                    </div>
                  ) : detail && detail.queueName === q.name ? (
                    <>
                      {/* Failed jobs */}
                      {detail.failed.length > 0 && (
                        <FailedJobsSection
                          jobs={detail.failed}
                          queueName={q.name}
                          search={failedSearch}
                          acting={acting}
                          onSearchChange={setFailedSearch}
                          onRetry={handleRetry}
                          onRemove={handleRemove}
                          onRetryAll={() => handleRetryAllFailed(q.name)}
                          onRemoveAll={() => handleRemoveAllFailed(q.name)}
                        />
                      )}

                      {/* Active jobs */}
                      {detail.active.length > 0 && (
                        <div>
                          <div className="text-[10px] font-medium text-blue-600 dark:text-blue-400 mb-1.5 flex items-center gap-1">
                            <Zap className="w-3 h-3" /> Active ({detail.active.length})
                          </div>
                          <div className="space-y-1">
                            {detail.active.map((job) => (
                              <div key={job.id} className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900/50 rounded-lg p-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] font-medium text-blue-800 dark:text-blue-400">{jobSummary(job)}</span>
                                  <span className="text-[9px] text-blue-600 dark:text-blue-400">
                                    {typeof job.progress === 'number' ? `${job.progress}%` : '...'}
                                  </span>
                                </div>
                                {typeof job.progress === 'number' && (
                                  <div className="w-full bg-blue-100 dark:bg-blue-950/40 rounded-full h-1.5">
                                    <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${job.progress}%` }} />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Waiting jobs */}
                      {detail.waiting.length > 0 && (
                        <div>
                          <div className="text-[10px] font-medium text-amber-600 mb-1.5 flex items-center gap-1">
                            <Pause className="w-3 h-3" /> Waiting ({detail.counts.waiting})
                          </div>
                          <div className="space-y-1">
                            {detail.waiting.slice(0, 5).map((job) => (
                              <div key={job.id} className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-lg p-1.5 flex items-center justify-between">
                                <span className="text-[10px] text-amber-800 dark:text-amber-400 truncate">{jobSummary(job)}</span>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  {job.timestamp && <span className="text-[9px] text-amber-500">{timeAgo(job.timestamp)}</span>}
                                  <button
                                    onClick={() => handleRemove(q.name, job.id)}
                                    className="text-gray-400 dark:text-gray-500 hover:text-red-500"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            {detail.counts.waiting > 5 && (
                              <div className="text-[9px] text-gray-400 dark:text-gray-500">+{detail.counts.waiting - 5} more</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Delayed/scheduled */}
                      {detail.delayed.length > 0 && (
                        <div>
                          <div className="text-[10px] font-medium text-gray-600 dark:text-gray-300 mb-1.5 flex items-center gap-1">
                            <Timer className="w-3 h-3" /> Scheduled ({detail.delayed.length})
                          </div>
                          <div className="space-y-1">
                            {detail.delayed.slice(0, 5).map((job) => (
                              <div key={job.id} className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg p-1.5 flex items-center justify-between">
                                <span className="text-[10px] text-gray-700 dark:text-gray-200 truncate">{jobSummary(job)}</span>
                                <span className="text-[9px] text-gray-500 dark:text-gray-400">
                                  {job.timestamp ? timeAgo(job.timestamp) : '--'}
                                </span>
                              </div>
                            ))}
                            {detail.delayed.length > 5 && (
                              <div className="text-[9px] text-gray-400 dark:text-gray-500">+{detail.delayed.length - 5} more</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Recent completed */}
                      {detail.completed.length > 0 && (
                        <div>
                          <div className="text-[10px] font-medium text-green-600 dark:text-green-400 mb-1.5 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Recent Completed
                          </div>
                          <div className="space-y-1">
                            {detail.completed.slice(0, 5).map((job) => (
                              <div key={job.id} className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900/50 rounded-lg p-1.5 flex items-center justify-between">
                                <span className="text-[10px] text-green-700 dark:text-green-400 truncate">{jobSummary(job)}</span>
                                <span className="text-[9px] text-green-600 dark:text-green-400" title={job.finishedOn ? new Date(job.finishedOn).toLocaleString() : ''}>
                                  {job.finishedOn ? timeAgo(job.finishedOn) : '--'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Drain button */}
                      {(detail.counts.waiting > 0 || detail.counts.failed > 0) && (
                        <div className="flex justify-end pt-1">
                          <button
                            onClick={() => handleDrain(q.name)}
                            disabled={acting === `drain-${q.name}`}
                            className="text-[10px] text-red-500 hover:text-red-700 dark:hover:text-red-400 flex items-center gap-1"
                          >
                            {acting === `drain-${q.name}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                            Drain queue
                          </button>
                        </div>
                      )}
                    </>
                  ) : null}

                  {/* Refresh detail */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => fetchQueueDetail(q.name)}
                      className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Failed Jobs Section ────────────────────── */

function FailedJobsSection({
  jobs,
  queueName,
  search,
  acting,
  onSearchChange,
  onRetry,
  onRemove,
  onRetryAll,
  onRemoveAll,
}: {
  jobs: QueueJob[];
  queueName: string;
  search: string;
  acting: string | null;
  onSearchChange: (v: string) => void;
  onRetry: (queueName: string, jobId: string) => void;
  onRemove: (queueName: string, jobId: string) => void;
  onRetryAll: () => void;
  onRemoveAll: () => void;
}) {
  const filtered = useMemo(() => {
    if (!search) return jobs;
    const q = search.toLowerCase();
    return jobs.filter((j) =>
      j.name.toLowerCase().includes(q) ||
      j.failedReason?.toLowerCase().includes(q) ||
      jobSummary(j).toLowerCase().includes(q)
    );
  }, [jobs, search]);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-medium text-red-600 dark:text-red-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Failed ({jobs.length})
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onRetryAll}
            disabled={acting === `retry-all-${queueName}`}
            className="px-1.5 py-0.5 text-[9px] font-medium border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50"
          >
            {acting === `retry-all-${queueName}` ? <Loader2 className="w-2.5 h-2.5 animate-spin inline" /> : null}
            Retry All
          </button>
          <button
            onClick={onRemoveAll}
            disabled={acting === `remove-all-${queueName}`}
            className="px-1.5 py-0.5 text-[9px] font-medium border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50"
          >
            Remove All
          </button>
        </div>
      </div>

      {/* Search */}
      {jobs.length > 3 && (
        <div className="relative mb-1.5">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-300" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Filter failed jobs..."
            className="w-full pl-6 pr-2 py-1 text-[10px] border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-300 placeholder:text-gray-300"
          />
        </div>
      )}

      <div className="space-y-1 max-h-60 overflow-auto">
        {filtered.map((job) => (
          <div key={job.id} className="bg-white dark:bg-gray-900 border border-red-200 dark:border-red-900/50 rounded-lg p-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] font-medium text-red-800 dark:text-red-400">{jobSummary(job)}</div>
                <div className="text-[9px] text-red-600 dark:text-red-400 truncate">{job.failedReason}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  {job.timestamp && (
                    <span className="text-[9px] text-gray-400 dark:text-gray-500" title={new Date(job.timestamp).toLocaleString()}>
                      {timeAgo(job.timestamp)}
                    </span>
                  )}
                  {job.attemptsMade != null && job.attemptsMade > 1 && (
                    <span className="text-[9px] text-red-400">{job.attemptsMade} attempts</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => onRetry(queueName, job.id)}
                  disabled={acting === `retry-${job.id}`}
                  className="px-1.5 py-0.5 text-[9px] font-medium bg-white dark:bg-gray-900 border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50"
                >
                  {acting === `retry-${job.id}` ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Retry'}
                </button>
                <button
                  onClick={() => onRemove(queueName, job.id)}
                  disabled={acting === `remove-${job.id}`}
                  className="px-1.5 py-0.5 text-[9px] font-medium bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {search && filtered.length === 0 && (
          <div className="text-[9px] text-gray-400 dark:text-gray-500 text-center py-2">No failed jobs matching "{search}"</div>
        )}
      </div>
    </div>
  );
}

/* ─── Summary Card ────────────────────────────── */

function SummaryCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  color: 'blue' | 'amber' | 'red' | 'gray' | 'green';
}) {
  const colorMap = {
    blue:  'text-blue-600 dark:text-blue-400',
    amber: 'text-amber-600',
    red:   'text-red-600 dark:text-red-400',
    gray:  'text-gray-600 dark:text-gray-300',
    green: 'text-green-600 dark:text-green-400',
  };
  const iconMap = {
    blue:  'text-blue-400',
    amber: 'text-amber-400',
    red:   'text-red-400',
    gray:  'text-gray-400 dark:text-gray-500',
    green: 'text-green-400',
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${iconMap[color]}`} />
        <span className="text-xs text-gray-400 dark:text-gray-500">{label}</span>
      </div>
      <div className={`text-lg font-semibold ${colorMap[color]}`}>{value.toLocaleString()}</div>
    </div>
  );
}
